import { isSiteCompositeStale, UNAVAILABLE_COMPOSITE_ANALYSIS_CONTEXT } from '../../lib/portfolio/compositeScorer';
import { siteReportBodiesSettled } from '../../lib/reportListHydration';
import { savedScreeningReportContexts, type SavedScreeningReportContext } from '../../lib/pathToGo/savedScreeningContext';
/**
 * twoActDerivation — the data spine of the "Two-Act Stage" dashboard hero
 * (slim pulse strip → POWER-MIX ORBIT → LOAD HORIZON; design source
 * docs/design/round7-dashlayout-1-twoact.html).
 *
 * EVERYTHING here derives from REAL store data — GAP-honest by construction:
 *
 *   - Orbit ring position reuses the SAME distance-to-GO derivation the
 *     Decision Queue uses (`buildDecisionCandidates` below is the EXTRACTED
 *     candidate builder `useDecisionQueue` itself consumes — one source, no
 *     forked scoring). Adequate sites position by `distanceToGo(decisionScore)`;
 *     verdict-withheld sites position by pillar coverage, exactly mirroring the
 *     queue's sort policy.
 *   - Power-mix arcs come ONLY from real per-phase power-source fields
 *     (`powerSourceConfig.componentParams` increments via the
 *     `perSourceIncrementMW` SSOT, or a single unambiguous source). A site with
 *     no mix data yields `mix: null` → the orbit renders NO arcs for it.
 *   - The load horizon stacks REAL phase MW increments (`phaseIncrementMW`
 *     SSOT) at REAL phase CODs (`effectivePhaseTimeline`). Bucket-only or
 *     undecided CODs become PROVISIONAL events (hatched downstream, never
 *     asserted); sites with no chartable phase data are returned as `notes`
 *     for the orbit-only footnote.
 *
 * This module must NOT be folded into useDashboardData.ts: the page-level
 * integration test mocks that module wholesale, and these helpers are consumed
 * by components that must keep working under that mock. useDashboardData
 * imports FROM here (never the reverse).
 */
import React from 'react';
import { resolveSiteStudyArea } from '../../lib/siteStudyArea';
import { siteHasUsableLocation } from '../../lib/siteBoundaryGeometry';
import { useStore } from '../../store';
import type { SiteEntity } from '../../types/site';
import type { DecisionStageStatus, ScreeningOutcome, SiteComposite } from '../../types/portfolioComposite';
import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import type {
  ProjectIntent,
  ProjectPhase,
  PowerSourceKey,
  PhasePowerComponentKey,
  SiteRagReport,
} from '../../types/siteRag';
import {
  deriveSiteVerdict,
  type DerivedSiteVerdict,
} from '../../lib/portfolio/deriveSiteVerdict';
import type { DecisionBandConfig } from '../../lib/portfolio/decisionBands';
import { dashboardScopePredicate } from '../../lib/portfolio/dashboardSiteScope';
import { distanceToGo, GO_DECISION_CEILING } from '../../lib/pathToGo/distanceToGo';
import { PROMOTE_SCENARIO_HEADLINE, STAGE2_DECISIONS_CTA } from '../../lib/pathToGo/stageActionCopy';
import {
  effectivePhases,
  effectivePhaseTimeline,
  phaseIncrementMW,
  perSourceIncrementMW,
  TIMELINE_BUCKET_LABEL,
} from '../../lib/projectIntent';

/* ── Decision candidates — the queue's scoring, extracted for reuse ───────── */

/**
 * One scoped site with a computed composite, scored exactly the way the
 * Decision Queue scores it. (Extracted verbatim from `useDecisionQueue`;
 * the queue now consumes THIS builder so the two surfaces cannot drift.)
 */
export interface DecisionCandidate {
  site: SiteEntity;
  composite: SiteComposite;
  /** Adequacy-gated verdict — the SAME deriveSiteVerdict every surface uses. */
  verdict: DerivedSiteVerdict;
  /** Distance above the GO ceiling on the decision scale (0 = at/better than GO). */
  distance: number;
}

export interface DecisionCandidateSet {
  candidates: DecisionCandidate[];
  /** Scoped sites with no composite yet (excluded until Recompute fills them). */
  notComputedCount: number;
}

/** Build the scored candidate universe from already-scoped sites. Pure. */
export function buildDecisionCandidates(
  scopedSites: readonly SiteEntity[],
  composites: Record<string, SiteComposite | undefined>,
  bandConfig: DecisionBandConfig | null | undefined,
): DecisionCandidateSet {
  let notComputedCount = 0;
  const candidates: DecisionCandidate[] = [];
  for (const site of scopedSites) {
    const composite = composites[site.id];
    if (!composite) {
      notComputedCount += 1;
      continue;
    }
    candidates.push({
      site,
      composite,
      verdict: deriveSiteVerdict(composite, null, bandConfig ?? undefined),
      distance: distanceToGo(composite.decisionScore),
    });
  }
  return { candidates, notComputedCount };
}

/**
 * The queue's sort policy, verbatim: banded (data-adequate) sites first,
 * nearest-to-GO ascending; verdict-withheld sites after, highest pillar
 * coverage first (they are the closest to EARNING a verdict).
 */
export function compareDecisionCandidates(a: DecisionCandidate, b: DecisionCandidate): number {
  const adequacyRank = (v: DerivedSiteVerdict): number => (v.dataAdequate ? 0 : 1);
  const rankDiff = adequacyRank(a.verdict) - adequacyRank(b.verdict);
  if (rankDiff !== 0) return rankDiff;
  if (a.verdict.dataAdequate) return a.distance - b.distance;
  return (b.verdict.coverageRatio ?? 0) - (a.verdict.coverageRatio ?? 0);
}

/* ── Power mix — real per-phase source MW only ────────────────────────────── */

export type MixCategory = 'btm' | 'grid' | 'nuclear';

/** MW per mix category. `total` is the categorized sum (> 0 by contract). */
export interface PowerMixMW {
  btm: number;
  grid: number;
  nuclear: number;
  total: number;
}

/** Granular per-phase component → mix category. Battery/storage rides BTM
 *  (it is on-site plant, not an import and not nuclear). */
const COMPONENT_MIX_CATEGORY: Record<PhasePowerComponentKey, MixCategory> = {
  'recip': 'btm',
  'gas-turbine': 'btm',
  'battery': 'btm',
  'grid': 'grid',
  'solar-bess': 'btm',
  'wind-bess': 'btm',
  'smr': 'nuclear',
  'large-nuclear': 'nuclear',
};

/** Coarse phase-level power source → mix category. `hybrid` is resolvable only
 *  through componentParams; `undecided` is no data. Both → null. */
const SOURCE_MIX_CATEGORY: Partial<Record<PowerSourceKey, MixCategory>> = {
  'grid': 'grid',
  'gas-cc': 'btm',
  'gas-sc': 'btm',
  'solar-bess': 'btm',
  'wind-bess': 'btm',
  'smr': 'nuclear',
  'large-nuclear': 'nuclear',
};

/**
 * Per-phase MW attribution to mix categories — REAL fields only, no
 * assumptions:
 *
 *   1. AND-mode hybrid with componentParams → per-source increments via the
 *      `perSourceIncrementMW` SSOT (cumulative convention respected).
 *   2. A SINGLE selected component → the phase increment belongs to it
 *      (unambiguous even without componentParams).
 *   3. A coarse single `powerSource` → the phase increment belongs to it.
 *   4. OR-mode alternatives / undecided / hybrid-without-params → contributes
 *      NOTHING (the mix is genuinely not yet decided — never guessed).
 */
function phaseMixContributions(
  phases: readonly ProjectPhase[],
  i: number,
): Array<{ category: MixCategory; mw: number }> {
  const phase = phases[i];
  const cfg = phase.powerSourceConfig;
  const out: Array<{ category: MixCategory; mw: number }> = [];

  const hasParams =
    cfg?.componentParams != null
    && cfg.components.some((c) => {
      const mw = cfg.componentParams?.[c]?.capacityMW;
      return typeof mw === 'number' && Number.isFinite(mw) && mw > 0;
    });

  if (cfg && cfg.components.length > 0 && hasParams && (cfg.mixMode === 'and' || cfg.components.length === 1)) {
    for (const key of cfg.components) {
      const inc = perSourceIncrementMW(phases, key, i);
      if (inc != null && inc > 0) out.push({ category: COMPONENT_MIX_CATEGORY[key], mw: inc });
    }
    return out;
  }

  if (cfg && cfg.components.length === 1) {
    const inc = phaseIncrementMW(phases, i);
    if (inc != null && inc > 0) out.push({ category: COMPONENT_MIX_CATEGORY[cfg.components[0]], mw: inc });
    return out;
  }

  // OR-mode multi-component alternatives: the user is still choosing — no
  // assertion. Fall through to the coarse source only when there is no config.
  if (cfg && cfg.components.length > 1) return out;

  const coarse = SOURCE_MIX_CATEGORY[phase.powerSource];
  if (coarse) {
    const inc = phaseIncrementMW(phases, i);
    if (inc != null && inc > 0) out.push({ category: coarse, mw: inc });
  }
  return out;
}

/**
 * Derive a site's power mix (MW per category) from its intent phases.
 * Returns null when NO phase carries categorizable real MW — the orbit then
 * renders no mix arcs for the site (GAP-honest).
 */
export function derivePowerMix(intent: ProjectIntent | undefined | null): PowerMixMW | null {
  const view = effectivePhases(intent);
  const phases = view.phases;
  const mix: PowerMixMW = { btm: 0, grid: 0, nuclear: 0, total: 0 };
  for (let i = 0; i < phases.length; i++) {
    for (const { category, mw } of phaseMixContributions(phases, i)) {
      mix[category] += mw;
    }
  }
  mix.total = mix.btm + mix.grid + mix.nuclear;
  return mix.total > 0 ? mix : null;
}

/* ── Orbit data ───────────────────────────────────────────────────────────── */

/**
 * The decision scale runs [1, 5] (see decisionBands DECISION_SCORE_MIN/MAX);
 * GO sits at ≤ 2.0, so the farthest a computed site can be from GO is 3.0.
 * Readiness = 1 − distance / MAX — the SAME scalar the queue ranks by,
 * re-expressed as a 0–1 ring position (1 = at GO, on the centre ring).
 */
export const MAX_DISTANCE_TO_GO = 5.0 - GO_DECISION_CEILING;

export interface OrbitSite {
  site: SiteEntity;
  verdict: DerivedSiteVerdict;
  /** The site's scored composite — drives the companion rail (pillars,
   *  confidenceTag, dataConfidenceBreakdown). Same object the queue scores. */
  composite: SiteComposite;
  /** The queue's scalar — distance above the GO ceiling (adequate sites). */
  distanceToGo: number;
  /**
   * Ring position 0–1, meaning ONE thing for EVERY site: readiness toward GO.
   * Adequate sites → 1 − distance/MAX (1 = at the GO ring). Verdict-withheld
   * sites have NOT earned any readiness, so they sit at ~0 on the outer
   * "unassessed" band — the radius is NEVER coverage (E1). Coverage is a
   * separate channel (the node gauge ring + `coverage` below).
   */
  readiness: number;
  /** Whether this site has earned a readiness position (data-adequate) or sits
   *  on the outer unassessed band (verdict withheld). The radius is the same
   *  readiness-to-GO axis in both cases — never a second meaning. */
  readinessKind: 'readiness' | 'unassessed';
  /**
   * Where the site sits in the TWO-STAGE DEVELOPER DECISION PIPELINE — the
   * ring it orbits on (see `pipelineColumnOf`) and how far through that stage it
   * is. Read off the composite's decision hold + per-stage checklist.
   */
  stage: OrbitStage;
  /** Saved library types, independent of qualification and stage completion. */
  savedAnalysisTypes?: ReportTypeId[];
  /** Read-only context from loaded, explicitly referenced screening reports. */
  screeningReportContexts?: SavedScreeningReportContext[];
  /** Evidence coverage 0–1 (fraction of decision pillars with real data) — the
   *  SEPARATE channel rendered as the cyan node gauge ring + "Coverage NN%". */
  coverage: number;
  /** Ultimate campus capacity (cumulative convention) — node size. Null = unknown. */
  capacityMW: number | null;
  /** Real power mix or null (→ no arcs). */
  mix: PowerMixMW | null;
}

export interface OrbitData {
  sites: OrbitSite[];
  /** Scoped sites with no composite yet — shown as an honest counter, never as nodes. */
  notComputedCount: number;
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

/** Resolve a site's headline MW the same way the hero pulse does. */
export function siteCapacityMW(
  site: SiteEntity,
  composite: SiteComposite | undefined,
): number | null {
  const phased = effectivePhases(site.projectIntent).totalCapacityMW;
  if (phased != null && Number.isFinite(phased) && phased > 0) return phased;
  const fallback = composite?.capacityMW;
  return typeof fallback === 'number' && Number.isFinite(fallback) && fallback > 0
    ? fallback
    : null;
}

/**
 * Ring position for a verdict-withheld site. It has NOT earned any readiness,
 * so it sits on the outer "unassessed" band — a hair inside the rim so the node
 * and its gauge ring are not clipped by the outer ring (matches the mock's
 * outer-band nodes). Coverage is shown SEPARATELY (the gauge), never here.
 */
const UNASSESSED_READINESS = 0.04;

/* ── Two-stage pipeline placement ─────────────────────────────────────────── */

/** The Decision Pipeline board's columns — the pipeline, left → right. */
export type PipelineColumn = 'unassessed' | 'stage1' | 'stage2' | 'decided';

export type OrbitStageKind = 'unassessed' | 'screening' | 'validating' | 'decided';

export interface OrbitStage {
  kind: OrbitStageKind;
  /** Required analyses of the stage in play: present / required. 0/0 when unknown. */
  done: number;
  of: number;
  /**
   * Short status ("Stage 1 · 2/4", "Stage 2 · 0/4") — the pipeline card's
   * accessible name. A Stage 2 waiting on its scenario reads like any other
   * Stage 2 count: the card shows no "Scenario needed" badge (owner,
   * 2026-09-30), and its next step already names the scenario.
   */
  label: string;
  /** Stage 2 cannot start until a generation scenario is promoted (the
   *  composite's `stage-2-locked` hold) — the rail names that next move. */
  needsScenario: boolean;
  /** The screening result, once every Stage 1 report is present (own vocabulary, never a band). */
  screening?: { outcome: ScreeningOutcome; concernTypes: ReportTypeId[] };
}

function stageProgress(stage: DecisionStageStatus | undefined): { done: number; of: number } {
  return { done: stage?.presentTypes.length ?? 0, of: stage?.requiredTypes.length ?? 0 };
}

/** Progress follows completed analysis milestones; final-verdict adequacy stays independent. */
export function deriveOrbitStage(composite: SiteComposite, verdict: DerivedSiteVerdict): OrbitStage {
  const stages = composite.decisionStages;
  const screening = stages?.screening.outcome
    ? { screening: { outcome: stages.screening.outcome, concernTypes: stages.screening.concernTypes ?? [] } }
    : {};
  if (verdict.band != null) {
    const { done, of } = stageProgress(stages?.validation);
    return { kind: 'decided', done, of, label: verdict.bandLabel, needsScenario: false, ...screening };
  }
  const screened = stageProgress(stages?.screening);
  const validated = stageProgress(stages?.validation);
  // Optional sentiment and older environmental reports cannot place a site
  // beyond early screening. Placement follows the current Stage 1 checklist.
  const hasAnalysis = stages?.screening.presentTypes.includes('feasibility') === true;
  if (!hasAnalysis) return { kind: 'unassessed', done: 0, of: screened.of, label: 'No current screening', needsScenario: false };
  // A completed no-stance screen can leave evidence unmeasured. Its band
  // remains withheld, while the workflow advances to validation inputs.
  if (screened.of > 0 && screened.done === screened.of
    && composite.decisionHold?.code !== 'power-strategy-undeclared') {
    const needsScenario = stages?.validation.locked === true;
    return {
      kind: 'validating', ...validated, needsScenario, ...screening,
      label: validated.of > 0 ? `Stage 2 · ${validated.done}/${validated.of}` : 'Stage 2 · validation',
    };
  }
  switch (composite.decisionHold?.code) {
    case 'stage-2-locked':
    case 'stage-2-incomplete':
      return {
        kind: 'validating', ...validated,
        label: validated.of > 0 ? `Stage 2 · ${validated.done}/${validated.of}` : 'Stage 2 · validation',
        needsScenario: composite.decisionHold.code === 'stage-2-locked', ...screening,
      };
    case 'power-strategy-undeclared':
      return { kind: 'screening', ...screened, label: 'Screened · define project', needsScenario: false, ...screening };
    case 'stage-1-incomplete':
      return { kind: 'screening', ...screened, label: screened.of > 0 ? `Stage 1 · ${screened.done}/${screened.of}` : 'Stage 1 · screening', needsScenario: false, ...screening };
    default:
      if (validated.done > 0) return { kind: 'validating', ...validated, label: `Stage 2 · ${validated.done}/${validated.of}`, needsScenario: false, ...screening };
      return { kind: 'screening', ...screened, label: screened.of > 0 ? `Stage 1 · ${screened.done}/${screened.of}` : 'Screening available', needsScenario: false, ...screening };
  }
}

/** Which column a site sits in. */
export function pipelineColumnOf(stage: OrbitStage): PipelineColumn {
  switch (stage.kind) {
    case 'unassessed': return 'unassessed';
    case 'screening': return 'stage1';
    case 'validating': return 'stage2';
    case 'decided': return 'decided';
  }
}

const BAND_ORDER: Record<string, number> = { go: 0, conditional: 1, 'conditional-high': 2, 'no-go': 3 };

/**
 * Sort key within a column — the site closest to LEAVING the column first.
 * Unassessed: the most screening already on file; screening / validating:
 * the largest share current (a site still awaiting its scenario trails);
 * decided: GO first, then by distance to GO.
 */
export function pipelineRank(orbitSite: OrbitSite): number {
  const { stage, composite, verdict } = orbitSite;
  switch (stage.kind) {
    case 'unassessed':
      return -((composite.decisionStages?.screening.presentTypes.length ?? 0) * 10 + orbitSite.coverage);
    case 'screening':
      return -(stage.of > 0 ? stage.done / stage.of : 0);
    case 'validating':
      return stage.needsScenario ? 1 : -(stage.of > 0 ? stage.done / stage.of : 0);
    case 'decided':
      return (BAND_ORDER[verdict.band ?? 'no-go'] ?? 3) + Math.min(9, orbitSite.distanceToGo) / 10;
  }
}

/**
 * The site tab that owns a pipeline next step — where the rail's Resolve
 * lands, so its label ("Run Environmental → Analyses") and the copy above it
 * name the place the click opens. `decision` is the site's decision surface,
 * resolved at navigation time (the promoted Decision tab, or Analyses when it
 * is rolled back — see siteDecisionTab); it is the successor of the retired
 * Path to GO hub and lists what still holds GO for everything without a more
 * specific home.
 */
export type OrbitNextStepTarget = 'map' | 'intent' | 'evidence' | 'reports' | 'decision';

export interface UnassessedNext {
  why: string;
  next: string;
  cta: string;
  target: OrbitNextStepTarget;
}

/** Every required early-screening analysis exists (presence, like the hold). */
function screeningComplete(composite: SiteComposite): boolean {
  const screening = composite.decisionStages?.screening;
  return !!screening && screening.requiredTypes.length > 0 && screening.missingTypes.length === 0;
}

function studyAreaReview(site: SiteEntity): UnassessedNext | null {
  if (!site.studyArea) return null;
  try { resolveSiteStudyArea(site); return null; }
  catch { return { why: 'Study area needs review', next: 'A selected polygon is missing, edited or not yet chosen. Review the map selection before running a study for this footprint.', cta: 'Review study area', target: 'map' }; }
}

/** Shared progress explanation; historical links never stand in for completed analyses. */
export function unassessedNext(orbitSite: OrbitSite): UnassessedNext {
  const { site, composite, verdict, stage } = orbitSite;
  const areaIssue = studyAreaReview(site);
  if (areaIssue) return areaIssue;
  const hasAnalysis = stage.kind !== 'unassessed';
  if (!hasAnalysis && !siteHasUsableLocation(site)) return { why: 'Location needed', next: 'Add a valid site location to begin public-source screening.', cta: 'Set site location', target: 'map' };
  if (site.sedStatus === 'bootstrapping') return {
    why: hasAnalysis ? 'Refreshing public evidence' : 'Collecting public evidence',
    next: hasAnalysis ? 'Earlier analyses remain available while source collection runs. Review their currency after the update.' : 'Public sources are being collected. Project intent and supporting documents can be added after the first screening findings.',
    cta: hasAnalysis ? 'Review screening progress' : 'Start screening',
    target: 'reports',
  };
  if (site.sedStatus === 'failed') return { why: 'Evidence collection failed', next: site.sedFailureReason || 'Review the failed collection and any available source evidence before retrying the analysis.', cta: 'Review collection failure', target: 'evidence' };
  if (!hasAnalysis) {
    const savedCount = orbitSite.savedAnalysisTypes?.length ?? 0;
    if (savedCount > 0) return {
      why: 'Saved analyses need review',
      next: `${savedCount} analysis type${savedCount === 1 ? ' is' : 's are'} saved. None currently completes a screening milestone. Open the site's Analyses to review quality holds, changed evidence and refresh requirements.`,
      cta: 'Review saved analyses',
      target: 'reports',
    };
    const links = site.reportIds?.length ?? 0;
    return {
      why: links > 0 ? 'Historical report links need review' : 'No analysis yet',
      next: links > 0
        ? `${links} historical report link${links === 1 ? ' is' : 's are'} recorded, but no analysis contributes to this overview. Open Reports to inspect the history or start a new screening assessment.`
        : 'Start with the site location. Screening identifies evidenced constraints and gaps before the project design is settled.',
      cta: 'Start screening',
      target: 'reports',
    };
  }
  const hasUnreadablePillar = ['power', 'grid', 'water', 'land', 'procurement', 'environmental', 'public-sentiment', 'fiscal', 'regulatory'].some(
    id => composite.pillars?.[id as import('../../lib/portfolio/pillarRegistry').PillarId]?.dedicatedSourceUnreadable
  );
  if (hasUnreadablePillar) {
    return {
      why: 'Analysis unreadable / held',
      next: 'One or more required analysis documents could not be read or are held by a reviewer. Please reload the reports or review their quality holds.',
      cta: 'Review analysis holds',
      target: 'reports',
    };
  }
  if (composite.decisionHold && composite.decisionHold.code !== 'power-strategy-undeclared' && verdict.dataAdequate !== false) {
    const isStage1Hold = composite.decisionHold.code === 'stage-1-incomplete';
    const isStage2Hold = composite.decisionHold.code === 'stage-2-locked' || composite.decisionHold.code === 'stage-2-incomplete';
    const activeStageMatches = (stage.kind === 'validating' && isStage2Hold) || (stage.kind !== 'validating' && isStage1Hold);

    if (activeStageMatches) {
      return {
        why: composite.decisionHold.label,
        next: composite.decisionHold.reason,
        cta: `Review ${stage.kind === 'validating' ? 'validation' : 'screening'} hold`,
        target: 'decision',
      };
    }
  }
  if (Date.parse(composite.lastComputedAt) === 0) return { why: 'Evidence summary needs refresh', next: 'Site inputs changed after this overview was computed. Check the current analysis plan before relying on earlier results.', cta: 'Review current analyses', target: 'reports' };
  // The power strategy is a Stage 2 decision: ask for it only once every
  // required screening analysis exists (screening compares every option).
  if (composite.decisionHold?.code === 'power-strategy-undeclared' && screeningComplete(composite)) return { why: 'Screening complete · Stage 2 decisions', next: 'Use the screening findings to decide the power supply approach and the other Stage 2 decisions; each shows the evidence for and against its options.', cta: STAGE2_DECISIONS_CTA, target: 'intent' };
  if (stage.kind === 'validating') return { why: 'Validation requires inputs', next: composite.decisionHold?.reason ?? 'Review the current engineering and financial analysis requirements. A final decision still needs sufficient evidence.', cta: 'Review validation requirements', target: 'decision' };
  return {
    why: verdict.dataAdequate ? 'Screening available' : 'Screening available with gaps',
    next: verdict.dataAdequate ? 'Continue the required analysis sequence and review the screening findings.' : 'Screening findings are available. Missing or uncertain evidence still limits a final decision; review the analysis plan for the next useful investigation.',
    cta: 'Review screening findings',
    target: 'reports',
  };
}

function shortLabel(type: ReportTypeId): string {
  return REPORT_TYPE_REGISTRY[type]?.shortLabel ?? type;
}

/**
 * The ONE next step for a site, in pipeline order — the same move the site's
 * Home next action leads with: the first outstanding screening analysis, the
 * promoted scenario, the first outstanding validation analysis, the decision —
 * and the tab that owns it (`target`, where the rail's Resolve lands). Running
 * or reviewing an analysis lives on Analyses, declaring the power strategy on
 * Intent, the study area on the Site Map; a scenario, a hold or the decision
 * itself opens the decision surface, which routes each remaining item.
 */
export function nextStepFor(orbitSite: OrbitSite): { label: string; target: OrbitNextStepTarget } {
  const { stage, composite, verdict } = orbitSite;
  const stages = composite.decisionStages;
  const areaIssue = verdict.band == null ? studyAreaReview(orbitSite.site) : null;
  if (areaIssue) return { label: areaIssue.cta, target: areaIssue.target };
  if (stage.kind === 'screening' && composite.decisionHold?.code === 'power-strategy-undeclared' && screeningComplete(composite)) {
    return { label: STAGE2_DECISIONS_CTA, target: 'intent' };
  }
  switch (stage.kind) {
    case 'unassessed': {
      const next = unassessedNext(orbitSite);
      return { label: next.cta, target: next.target };
    }
    case 'screening': {
      const missing = stages?.screening.missingTypes[0];
      return { label: missing ? `${orbitSite.savedAnalysisTypes?.includes(missing) ? 'Review' : 'Run'} ${shortLabel(missing)}` : 'Review screening findings', target: 'reports' };
    }
    case 'validating': {
      if (stage.needsScenario) return { label: PROMOTE_SCENARIO_HEADLINE, target: 'decision' };
      // The scenario is promoted (the hold is stage-2-incomplete), so Generation
      // & Impact is runnable — the same move the Home next action leads with.
      const missing = stages?.validation.missingTypes[0];
      if (missing && orbitSite.savedAnalysisTypes?.includes(missing)) return { label: `Review ${shortLabel(missing)}`, target: 'reports' };
      if (missing === 'power-availability') return { label: 'Run Generation & Impact', target: 'reports' };
      return { label: missing ? `Run ${shortLabel(missing)}` : 'Run mature validation', target: 'reports' };
    }
    case 'decided':
      if (verdict.band === 'no-go') {
        return { label: stage.screening?.outcome === 'declined' ? 'Declined at screening' : 'Review the decline', target: 'decision' };
      }
      return { label: 'Review binding constraints', target: 'decision' };
  }
}

function toOrbitSite(candidate: DecisionCandidate, reports: readonly SiteRagReport[]): OrbitSite {
  const { site, composite, verdict, distance } = candidate;
  // Readiness means ONE thing for every site: distance toward the GO ring.
  // Withheld sites have earned no readiness → outer unassessed band (E1).
  const readiness = verdict.dataAdequate
    ? clamp01(1 - distance / MAX_DISTANCE_TO_GO)
    : UNASSESSED_READINESS;
  return {
    site,
    verdict,
    composite,
    distanceToGo: distance,
    readiness,
    readinessKind: verdict.dataAdequate ? 'readiness' : 'unassessed',
    stage: deriveOrbitStage(composite, verdict),
    savedAnalysisTypes: savedAnalysisTypesForSite(site, reports),
    screeningReportContexts: savedScreeningReportContexts(site, composite, reports),
    // Coverage is its OWN channel — the gauge ring sweep. Always reported as
    // the fraction of decision pillars with real data (verdict.coverageRatio).
    coverage: clamp01(verdict.coverageRatio ?? 0),
    capacityMW: siteCapacityMW(site, composite),
    mix: derivePowerMix(site.projectIntent),
  };
}

/** Count actual saved types once; old revisions and orphan links are not extra analyses. */
export function savedAnalysisTypesForSite(site: SiteEntity, reports: readonly SiteRagReport[]): ReportTypeId[] {
  const nameKey = (value: unknown) => typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, ' ') : '';
  const siteName = nameKey(site.name);
  const types = new Set<ReportTypeId>();
  for (const report of reports) {
    if (!(report.siteId === site.id || site.reportIds?.includes(report.id)
      || (!report.siteId && siteName && nameKey(report.siteName) === siteName))) continue;
    const type = report.reportType ?? 'feasibility';
    if (Object.prototype.hasOwnProperty.call(REPORT_TYPE_REGISTRY, type)) types.add(type);
  }
  return [...types].sort();
}

/** Pure projection used by the hook (and tests). */
export function buildOrbitData(
  scopedSites: readonly SiteEntity[],
  composites: Record<string, SiteComposite | undefined>,
  bandConfig: DecisionBandConfig | null | undefined,
  reports: readonly SiteRagReport[] = [],
): OrbitData {
  const { candidates, notComputedCount } = buildDecisionCandidates(
    scopedSites,
    composites,
    bandConfig,
  );
  candidates.sort(compareDecisionCandidates);
  return { sites: candidates.map(candidate => toOrbitSite(candidate, reports)), notComputedCount };
}

export function useOrbitData(
  customerId: string | null,
  /**
   * Optional view filter from the host (the engine's left-rail site filter:
   * search + company + verdict + data-scope). null/undefined = unfiltered.
   * Applied ON TOP of the authorization scope predicate — it can only
   * narrow, never widen, the visible estate.
   */
  visibleSiteIds?: ReadonlySet<string> | null,
): OrbitData {
  const sites = useStore((s) => s.siteRag.sites);
  const composites = useStore((s) => s.portfolio.composites);
  const reports = useStore((s) => s.siteRag.reports);
  const hydratedReportSiteIds = useStore((s) => s.siteRag.hydratedReportSiteIds);
  const analysisContexts = useStore((s) => s.portfolio.analysisContexts);
  const bandConfig = useStore((s) => s.portfolio.bandConfig);
  const isAdmin = useStore((s) => s.userRole === 'admin');
  const subtreeCompanies = useStore((s) => s.subtreeCompanies);

  return React.useMemo(() => {
    const inScope = dashboardScopePredicate(customerId, { isAdmin, subtreeCompanies });
    const scoped = sites.filter((s) => inScope(s) && (visibleSiteIds == null || visibleSiteIds.has(s.id)));
    const listedRows = reports as unknown as Record<string, unknown>[];
    const currentComposites = Object.fromEntries(scoped.flatMap(site => {
      const composite = composites[site.id];
      // The list pass reads the open site's bodies first and warms the rest
      // behind the loading gate. A site whose current heads are still on their
      // way cannot be fingerprinted (metadata-only rows are tagged by design),
      // so its saved composite stands unless it is missing or epoch-stamped —
      // the same rule ensurePortfolioComposites applies before recomputing.
      // Otherwise every card would read "Awaiting compute" until the whole
      // portfolio had landed. Likewise, while /api/decision-analyses/:siteId is
      // still in flight (site.id not yet in analysisContexts) and the persisted
      // composite was fingerprinted against a live analysis gate (|analyses:{gate:true),
      // do not transiently substitute UNAVAILABLE_COMPOSITE_ANALYSIS_CONTEXT
      // (|analyses:{gate:false}), which would blank the Orbit before hydration completes.
      const hasAnalysisContextLoaded = Object.prototype.hasOwnProperty.call(
        analysisContexts ?? {},
        site.id,
      );
      const awaitingAnalysisContext =
        !hasAnalysisContextLoaded
        && typeof composite?.inputsFingerprint === 'string'
        && composite.inputsFingerprint.includes('|analyses:{gate:true');
      const stale = (siteReportBodiesSettled(site, listedRows, hydratedReportSiteIds) && !awaitingAnalysisContext)
        ? isSiteCompositeStale(composite, undefined, {
            site, reports,
            analysisContext: analysisContexts?.[site.id] ?? UNAVAILABLE_COMPOSITE_ANALYSIS_CONTEXT,
          })
        : isSiteCompositeStale(composite, Number.POSITIVE_INFINITY, null);
      return stale ? [] : [[site.id, composite]];
    }));
    return buildOrbitData(scoped, currentComposites, bandConfig ?? null, reports);
  }, [customerId, sites, composites, reports, hydratedReportSiteIds, analysisContexts, bandConfig, isAdmin, subtreeCompanies, visibleSiteIds]);
}

/* ── Load horizon — energized-MW staircase from real phase MW + COD ───────── */

/** Conventional month length — same factor as projectIntent's MS_PER_MONTH. */
const MS_PER_MONTH = (365.25 / 12) * 24 * 60 * 60 * 1000;

/** Bucket-only CODs project to the bucket midpoint MONTHS from today — used
 *  ONLY to position a hatched provisional ribbon, never asserted as a date. */
const BUCKET_MIDPOINT_MONTHS: Record<'under-2yr' | '2-5yr' | 'over-5yr', number> = {
  'under-2yr': 12,
  '2-5yr': 42,
  'over-5yr': 72,
};

export interface HorizonEvent {
  siteId: string;
  siteName: string;
  phaseId: string;
  /** "P1", "P2", … (phase sequence). */
  phaseLabel: string;
  /** MW ADDED by this phase (increment SSOT — never the cumulative). */
  mw: number;
  /** Mix category of the increment ('unknown' when the source is undecided). */
  category: MixCategory | 'unknown';
  /** Exact ISO COD when firm; null for provisional events. */
  dateIso: string | null;
  /** Position on the time axis (epoch ms). For provisional events this is the
   *  bucket-midpoint ESTIMATE (or end-of-queue for undated) — display always
   *  goes through `dateLabel`, which never asserts a month. */
  dateMs: number;
  /** "Apr 2028" (firm) · "~2-5 years" (bucket) · "UNDATED" (no signal). */
  dateLabel: string;
  /** True → hatched ribbon + italic milestone; never part of the firm stack. */
  provisional: boolean;
}

export interface HorizonSiteNote {
  siteId: string;
  siteName: string;
  reason: 'no-phases' | 'no-phase-mw';
}

export interface HorizonData {
  /** Firm events sorted by COD, then provisional sorted by estimated position. */
  firmEvents: HorizonEvent[];
  provisionalEvents: HorizonEvent[];
  /** Σ event MW actually charted (firm + provisional). */
  chartedMW: number;
  /** Σ per-site ultimate campus MW — the FULL BUILD cap line. */
  fullBuildMW: number;
  /** Sites that cannot be charted (no phases / no phase MW) — orbit-only footnote. */
  notes: HorizonSiteNote[];
  /** Earliest firm event — the pulse strip's FIRST POWER. Null when nothing is dated. */
  firstPower: HorizonEvent | null;
}

function phaseCategory(phases: readonly ProjectPhase[], i: number): MixCategory | 'unknown' {
  const contributions = phaseMixContributions(phases, i);
  if (contributions.length === 0) return 'unknown';
  // Dominant category of the phase's REAL attributed MW.
  const byCategory = new Map<MixCategory, number>();
  for (const c of contributions) {
    byCategory.set(c.category, (byCategory.get(c.category) ?? 0) + c.mw);
  }
  let best: MixCategory | 'unknown' = 'unknown';
  let bestMw = 0;
  for (const [category, mw] of byCategory) {
    if (mw > bestMw) {
      best = category;
      bestMw = mw;
    }
  }
  return best;
}

/** Pure horizon projection over already-scoped sites. */
export function buildHorizonData(
  scopedSites: readonly SiteEntity[],
  now: Date = new Date(),
): HorizonData {
  const firm: HorizonEvent[] = [];
  const provisional: HorizonEvent[] = [];
  const undated: HorizonEvent[] = [];
  const notes: HorizonSiteNote[] = [];
  let fullBuildMW = 0;

  for (const site of scopedSites) {
    const view = effectivePhases(site.projectIntent);
    const siteName = site.name ?? 'Unknown site';
    fullBuildMW += view.totalCapacityMW ?? 0;

    // A legacy intent with neither MW nor COD has nothing to chart at all.
    const hasAnySignal = view.phases.some(
      (p) => (p.capacityMW != null && p.capacityMW > 0) || p.targetInServiceDate != null,
    );
    if (!hasAnySignal) {
      notes.push({ siteId: site.id, siteName, reason: 'no-phases' });
      continue;
    }

    let charted = 0;
    for (let i = 0; i < view.phases.length; i++) {
      const phase = view.phases[i];
      const mw = phaseIncrementMW(view.phases, i);
      if (mw == null || mw <= 0) continue; // MW TBD — cannot stack a height honestly.
      const timeline = effectivePhaseTimeline(phase, now);
      const base = {
        siteId: site.id,
        siteName,
        phaseId: phase.id,
        phaseLabel: `P${phase.sequence}`,
        mw,
        category: phaseCategory(view.phases, i),
      };
      if (timeline.isExact && timeline.inServiceDate) {
        firm.push({
          ...base,
          dateIso: timeline.inServiceDate,
          dateMs: new Date(timeline.inServiceDate).getTime(),
          dateLabel: timeline.displayLabel,
          provisional: false,
        });
      } else if (timeline.bucket !== 'undecided') {
        provisional.push({
          ...base,
          dateIso: null,
          dateMs: now.getTime() + BUCKET_MIDPOINT_MONTHS[timeline.bucket] * MS_PER_MONTH,
          dateLabel: `~${TIMELINE_BUCKET_LABEL[timeline.bucket]}`,
          provisional: true,
        });
      } else {
        undated.push({
          ...base,
          dateIso: null,
          dateMs: Number.NaN, // assigned below — after every dated event.
          dateLabel: 'UNDATED',
          provisional: true,
        });
      }
      charted += 1;
    }
    if (charted === 0) {
      notes.push({ siteId: site.id, siteName, reason: 'no-phase-mw' });
    }
  }

  firm.sort((a, b) => a.dateMs - b.dateMs);
  provisional.sort((a, b) => a.dateMs - b.dateMs);

  // Undated events ride the END of the queue — positioned after every dated
  // event (never interleaved into asserted territory), in site/phase order.
  const datedMax = Math.max(
    now.getTime(),
    ...firm.map((e) => e.dateMs),
    ...provisional.map((e) => e.dateMs),
  );
  undated.forEach((e, i) => {
    e.dateMs = datedMax + (i + 1) * 9 * MS_PER_MONTH;
  });

  const provisionalAll = [...provisional, ...undated];
  const chartedMW =
    firm.reduce((sum, e) => sum + e.mw, 0) + provisionalAll.reduce((sum, e) => sum + e.mw, 0);

  return {
    firmEvents: firm,
    provisionalEvents: provisionalAll,
    chartedMW,
    fullBuildMW,
    notes,
    firstPower: firm[0] ?? null,
  };
}

export function useHorizonData(
  customerId: string | null,
  /** Same view filter as useOrbitData — the two acts must show ONE estate. */
  visibleSiteIds?: ReadonlySet<string> | null,
): HorizonData {
  const sites = useStore((s) => s.siteRag.sites);
  const isAdmin = useStore((s) => s.userRole === 'admin');
  const subtreeCompanies = useStore((s) => s.subtreeCompanies);

  return React.useMemo(() => {
    const inScope = dashboardScopePredicate(customerId, { isAdmin, subtreeCompanies });
    const scoped = sites.filter((s) => inScope(s) && (visibleSiteIds == null || visibleSiteIds.has(s.id)));
    return buildHorizonData(scoped);
  }, [customerId, sites, isAdmin, subtreeCompanies, visibleSiteIds]);
}

/* ── Reduced motion ───────────────────────────────────────────────────────── */

/**
 * One-shot prefers-reduced-motion read (same pattern as ProductPreviewRotator).
 * The Two-Act components mirror it onto a `data-motion` attribute so the CSS
 * final-frame gate has a testable, non-media-query twin.
 */
export function usePrefersReducedMotion(): boolean {
  return React.useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  }, []);
}

/* ── Staircase geometry — contour + block polygons (pure, pixel-space) ────── */

/** A piecewise-linear "top of the stack" contour: sorted (x, v) breakpoints.
 *  `v` is in VALUE units (MW); the component maps v → y at render time. */
export interface ContourPoint {
  x: number;
  v: number;
}

/** Linear interpolation along the contour (clamped to its ends). */
export function contourValueAt(contour: readonly ContourPoint[], x: number): number {
  if (contour.length === 0) return 0;
  if (x <= contour[0].x) return contour[0].v;
  for (let i = 1; i < contour.length; i++) {
    const a = contour[i - 1];
    const b = contour[i];
    if (x <= b.x) {
      if (b.x === a.x) return b.v;
      const t = (x - a.x) / (b.x - a.x);
      return a.v + t * (b.v - a.v);
    }
  }
  return contour[contour.length - 1].v;
}

/**
 * Stack one block of `mw` onto the contour from `startX` to the contour's
 * right edge, with a linear leading ramp of `rampW` px. Returns the block's
 * polygon (bottom = OLD contour, top = NEW contour — so later blocks ride the
 * full shape of everything beneath them, exactly like the approved mock) plus
 * the new contour.
 */
export function addContourBlock(
  contour: readonly ContourPoint[],
  startX: number,
  mw: number,
  rampW: number,
): { polygon: ContourPoint[]; contour: ContourPoint[] } {
  const endX = contour[contour.length - 1]?.x ?? startX;
  const x0 = Math.min(Math.max(startX, contour[0]?.x ?? startX), endX);
  const ramp = Math.max(1, Math.min(rampW, endX - x0));
  const bump = (x: number): number => mw * Math.min(1, Math.max(0, (x - x0) / ramp));

  // Breakpoints of the NEW contour: old breakpoints ∪ ramp start/end.
  const xs = new Set<number>([x0, Math.min(x0 + ramp, endX)]);
  for (const p of contour) xs.add(p.x);
  const sortedXs = [...xs].sort((a, b) => a - b);

  const newContour: ContourPoint[] = sortedXs.map((x) => ({
    x,
    v: contourValueAt(contour, x) + (x >= x0 ? bump(x) : 0),
  }));

  // Polygon: old contour (bottom, left→right from x0) + new contour (top,
  // right→left back to x0). Both clipped to [x0, endX].
  const within = sortedXs.filter((x) => x >= x0);
  const bottom: ContourPoint[] = within.map((x) => ({ x, v: contourValueAt(contour, x) }));
  const top: ContourPoint[] = within
    .map((x) => ({ x, v: contourValueAt(newContour, x) }))
    .reverse();

  return { polygon: [...bottom, ...top], contour: newContour };
}
